import { useForm, useWatch } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useEffect } from 'react'
import { Loader2 } from 'lucide-react'
import { toast } from 'sonner'

import {
  Sheet,
  SheetBody,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import { Input } from '@/components/ui/input'
import { ChonkyButton } from '@/components/shared/chonky-button'
import { useCreateMember, useUpdateMember, useCampaigns } from '@/hooks'
import type { Member } from '@/lib/api'

const formSchema = z
  .object({
    name: z.string().min(2, 'Nome deve ter pelo menos 2 caracteres'),
    email: z.string().email('Email inválido'),
    externalId: z.string().min(1, 'ID Externo é obrigatório'),
    campaignId: z.string().min(1, 'Campanha é obrigatória'),
    phone: z.string().optional(),
    cpf: z.string().optional(),
    status: z.enum(['active', 'inactive', 'blocked']).optional(),
    useCustomCode: z.boolean(),
    referralCode: z.string().optional(),
  })
  .superRefine((data, ctx) => {
    if (data.useCustomCode) {
      if (!data.referralCode || data.referralCode.length < 3) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Mínimo 3 caracteres',
          path: ['referralCode'],
        })
      } else if (data.referralCode.length > 20) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Máximo 20 caracteres',
          path: ['referralCode'],
        })
      } else if (!/^[A-Z0-9-_]+$/.test(data.referralCode)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Apenas letras maiúsculas, números, hífens e underlines',
          path: ['referralCode'],
        })
      }
    }
  })

type FormValues = z.infer<typeof formSchema>

type MemberDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  member?: Member | null
}

export function MemberDialog({ open, onOpenChange, member }: MemberDialogProps) {
  const isEditing = !!member
  const { mutateAsync: createMember, isPending: isCreating } = useCreateMember()
  const { mutateAsync: updateMember, isPending: isUpdating } = useUpdateMember()
  const { data: campaigns, isLoading: isLoadingCampaigns } = useCampaigns()

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: '',
      email: '',
      externalId: '',
      campaignId: '',
      phone: '',
      cpf: '',
      status: 'active',
      useCustomCode: false,
      referralCode: '',
    },
    mode: 'onChange',
  })

  // Watch the switch state to show/hide input
  const useCustomCode = useWatch({ control: form.control, name: 'useCustomCode' })

  useEffect(() => {
    if (member) {
      form.reset({
        name: member.name,
        email: member.email,
        externalId: member.externalId,
        campaignId: member.campaignId,
        phone: member.phone || '',
        cpf: member.cpf || '',
        status: member.status,
        useCustomCode: !!member.referralCode,
        referralCode: member.referralCode || '',
      })
    } else {
      form.reset({
        name: '',
        email: '',
        externalId: '',
        campaignId: '',
        phone: '',
        cpf: '',
        status: 'active',
        useCustomCode: false,
        referralCode: '',
      })
    }
  }, [member, form, open])

  async function onSubmit(values: FormValues) {
    try {
      if (isEditing && member) {
        await updateMember({
          id: member.id,
          data: {
            name: values.name,
            phone: values.phone || undefined,
            campaignId: values.campaignId,
            status: values.status as Member['status'],
          },
        })
      } else {
        await createMember({
          name: values.name,
          email: values.email,
          externalId: values.externalId,
          campaignId: values.campaignId,
          phone: values.phone || undefined,
          cpf: values.cpf || undefined,
          referralCode: values.useCustomCode ? values.referralCode : undefined,
        })
      }
      onOpenChange(false)
      if (!isEditing) form.reset()
      toast.success(isEditing ? 'Membro atualizado!' : 'Membro criado com sucesso!')
    } catch (error) {
      console.error(error)
      if (error instanceof Error) {
        // Handle specific API error messages
        const msg = error.message.toLowerCase()
        if (msg.includes('cupom') && msg.includes('uso')) {
          form.setError('referralCode', {
            type: 'manual',
            message: 'Este código já está em uso.',
          })
          // If the field is hidden (switch off), we might want to alert the user anyway
          // But usually they would be typing it.
          // Since it's a constraint violation on the custom code, we assume they tried to set one.
          return
        }

        toast.error('Erro ao salvar membro', {
          description: error.message,
        })
      }
    }
  }

  const isPending = isCreating || isUpdating

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="sm:max-w-xl">
        <SheetHeader>
          <SheetTitle>{isEditing ? 'Editar Membro' : 'Novo Membro'}</SheetTitle>
          <SheetDescription>
            {isEditing
              ? 'Edite os dados do membro abaixo.'
              : 'Preencha os dados para cadastrar um novo membro.'}
          </SheetDescription>
        </SheetHeader>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="flex flex-col flex-1 overflow-hidden"
          >
            <SheetBody className="space-y-4">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Nome</FormLabel>
                    <FormControl>
                      <Input placeholder="João Silva" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="joao@email.com"
                          type="email"
                          {...field}
                          disabled={isEditing}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="externalId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>ID Externo</FormLabel>
                      <FormControl>
                        <Input placeholder="123456" {...field} disabled={isEditing} />
                      </FormControl>
                      <FormDescription className="text-xs">
                        ID no seu sistema (ex: Guru)
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="cpf"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>CPF</FormLabel>
                      <FormControl>
                        <Input placeholder="000.000.000-00" {...field} disabled={isEditing} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="phone"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Telefone</FormLabel>
                      <FormControl>
                        <Input placeholder="+55 11 99999-9999" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="rounded-lg border p-4">
                <FormField
                  control={form.control}
                  name="useCustomCode"
                  render={({ field }) => (
                    <div className="flex flex-row items-center justify-between mb-4">
                      <div className="space-y-0.5">
                        <FormLabel className="text-base">Personalizar Código</FormLabel>
                        <FormDescription>Criar um cupom personalizado (ex: RAFO5).</FormDescription>
                      </div>
                      <FormControl>
                        <Switch
                          checked={field.value}
                          onCheckedChange={field.onChange}
                          disabled={isEditing}
                        />
                      </FormControl>
                    </div>
                  )}
                />

                {useCustomCode && (
                  <FormField
                    control={form.control}
                    name="referralCode"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Cupom</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="RAFO5"
                            {...field}
                            disabled={isEditing}
                            className="uppercase"
                            onChange={(e) => field.onChange(e.target.value.toUpperCase())}
                            maxLength={20}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}
              </div>

              <FormField
                control={form.control}
                name="campaignId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Campanha</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                      value={field.value}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Selecione uma campanha" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {isLoadingCampaigns ? (
                          <div className="flex items-center justify-center p-2">
                            <Loader2 className="h-4 w-4 animate-spin" />
                          </div>
                        ) : (
                          campaigns?.map((campaign) => (
                            <SelectItem key={campaign.id} value={campaign.id}>
                              {campaign.name}
                            </SelectItem>
                          ))
                        )}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {isEditing && (
                <FormField
                  control={form.control}
                  name="status"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Status</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        defaultValue={field.value}
                        value={field.value}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Selecione o status" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="active">Ativo</SelectItem>
                          <SelectItem value="inactive">Inativo</SelectItem>
                          <SelectItem value="blocked">Bloqueado</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}
            </SheetBody>

            <SheetFooter>
              <ChonkyButton type="button" variant="secondary" onClick={() => onOpenChange(false)}>
                Cancelar
              </ChonkyButton>
              <ChonkyButton type="submit" loading={isPending}>
                {isEditing ? 'Salvar Alterações' : 'Criar Membro'}
              </ChonkyButton>
            </SheetFooter>
          </form>
        </Form>
      </SheetContent>
    </Sheet>
  )
}
